import { BaseDataCollector } from "src/core/base/BaseDataCollector";
import { ConfigItem, VariableConfig } from "src/core/model/TrackerConfig";
import {
  CookieOption,
  ElementOption,
  JavascriptOption,
  TrackerVariableType,
  UrlSelection,
} from "src/core/model/TrackerVariableType";
import { Logger } from "src/util/Logger";

/**
 * Collects screen/page activity variables: URL parts, cookies, elements, JS evaluation, view duration.
 * Mirrors Android ScreenActivityCollector + web-specific variable resolution from tracker-web_current.
 */
export class ScreenActivityCollector extends BaseDataCollector {
  private viewDurationMs = 0;
  private timerIntervalId: ReturnType<typeof setInterval> | null = null;

  collect(): Map<TrackerVariableType, unknown> {
    const data = new Map<TrackerVariableType, unknown>();

    this.collectIfNeeded(data, TrackerVariableType.VIEW_DURATION, () => this.viewDurationMs);

    return data;
  }

  /**
   * Resolve a variable from the current page context using the backend config's variable config.
   * This handles URL, ELEMENT, COOKIE, JAVASCRIPT, EVENT types.
   */
  resolveVariable(varConfig: VariableConfig, eventData?: Record<string, unknown>): unknown {
    try {
      switch (varConfig.type) {
        case TrackerVariableType.URL:
          return this.resolveUrl(varConfig.option as Record<string, unknown> | undefined);

        case TrackerVariableType.ELEMENT:
          return this.resolveElement(varConfig.option as ElementOption | undefined);

        case TrackerVariableType.COOKIE:
          return this.resolveCookie(varConfig.option as CookieOption | undefined);

        case TrackerVariableType.JAVASCRIPT:
          return this.resolveJavascript(varConfig.option as JavascriptOption | undefined);

        case TrackerVariableType.EVENT:
          return this.resolveEvent(varConfig, eventData);

        default:
          return null;
      }
    } catch (error) {
      Logger.warn(`Failed to resolve variable ${varConfig.name}:`, error);
      return null;
    }
  }

  // === Timer (view duration) ===

  startTimer(): void {
    if (this.timerIntervalId !== null) return;
    this.viewDurationMs = 0;
    this.timerIntervalId = setInterval(() => {
      this.viewDurationMs += 100;
    }, 100);
  }

  stopTimer(): void {
    if (this.timerIntervalId !== null) {
      clearInterval(this.timerIntervalId);
      this.timerIntervalId = null;
    }
  }

  resetTimer(): void {
    this.viewDurationMs = 0;
  }

  // === URL Resolution ===

  private resolveUrl(option?: Record<string, unknown>): string {
    const selection = (option?.selection as UrlSelection) ?? "full";
    const url = new URL(window.location.href);

    switch (selection) {
      case "host":
        return url.hostname;
      case "port":
        return url.port;
      case "path":
        return url.pathname;
      case "query":
        return url.search;
      case "fragment":
        return url.hash;
      case "protocol":
        return url.protocol;
      case "full":
      default:
        return url.href;
    }
  }

  // === Element Resolution ===

  private resolveElement(option?: ElementOption): string | null {
    if (!option?.cssSelector) return null;
    const el = document.querySelector(option.cssSelector);
    if (!el) return null;

    if (option.attribute) {
      return el.getAttribute(option.attribute);
    }
    return el.textContent;
  }

  // === Cookie Resolution ===

  private resolveCookie(option?: CookieOption): string | null {
    if (!option?.cookieName) return null;
    const cookies = document.cookie.split(";");
    for (const cookie of cookies) {
      const [name, ...valueParts] = cookie.split("=");
      if (name.trim() === option.cookieName) {
        const value = valueParts.join("=").trim();
        return option.decodeUrlCookie ? decodeURIComponent(value) : value;
      }
    }
    return null;
  }

  // === JavaScript Resolution (using new Function instead of eval) ===

  private resolveJavascript(option?: JavascriptOption): unknown {
    if (!option?.code) return null;
    try {
      // Use new Function() instead of eval() for slightly better isolation
      return new Function(`return (${option.code})`)();
    } catch (error) {
      Logger.warn("JavaScript variable evaluation failed:", error);
      return null;
    }
  }

  // === Event Resolution ===

  private resolveEvent(varConfig: VariableConfig, eventData?: Record<string, unknown>): unknown {
    if (!eventData) return null;
    return eventData[varConfig.name] ?? null;
  }

  /** Resolve all config-driven variables for a given config item. */
  resolveConfigVariables(config: ConfigItem, eventData?: Record<string, unknown>): Map<TrackerVariableType, unknown> {
    const data = new Map<TrackerVariableType, unknown>();

    for (const varConfig of config.variables) {
      const type = varConfig.type as TrackerVariableType;
      // Only resolve DOM/page-context types here
      if (
        type === TrackerVariableType.URL ||
        type === TrackerVariableType.ELEMENT ||
        type === TrackerVariableType.COOKIE ||
        type === TrackerVariableType.JAVASCRIPT ||
        type === TrackerVariableType.EVENT
      ) {
        const value = this.resolveVariable(varConfig, eventData);
        if (value !== null && value !== undefined) {
          data.set(type, value);
        }
      }
    }

    return data;
  }

  destroy(): void {
    this.stopTimer();
  }
}
